import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Form, TextField, Button, SelectBox, MultiSelect } from "../../primary";
import styles from "./assign.module.scss";
import { EDIT, CANCEL_ID, CANCEL_BTN, SUBMIT_ID, SAVE_BTN } from "../../../constants";
import editIcon from "../../../static/images/edit-icon-blue.png";
import rightArrow from "../../../static/images/right_arrow.svg";
import { getFacilitiesAsync, setUpdateStatus, reSetFacilitiesData, getAssignEmailAsyn, upDateformDetailsAsync } from "../../../app/customFormSlice";
import { getFacilityLabelByName } from "../../../utils";
import Loader from "../../../static/images/spinner.svg";
import useFacilityOptions from "../../../hooks/useFacilityOptions";
import ViewAssign from "./viewAssign";
import AsyncTagSelect from "../../secondary/AsyncTagSelect/AsyncTagSelect";
import { PERMISSIONS } from "../../../constants/permissions";

export const Assign = () => {
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.forms.formDetailsData);
  const facilitiesData = useSelector((state) => state.forms.orgData);
  const isFormLoading = useSelector((state) => state.forms.isFormLoading);

  const { handleSubmit, errors } = useForm({
    mode: "onBlur",
  });
  const [emailTags, setEmailTags] = useState([]);
  const [editable, setEditable] = useState(false);
  const [errorAssign, setErrorAssign] = useState(false);
  const [customerNumber, setCustomerNumber] = useState(formData?.assignInfo?.customerNumber);
  const [removeDefaultOrg, setRemoveDefaultOrg] = useState(true);
  const [org, setOrg] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [createGroup, setCreateGroup] = useState(false);
  const [customerNumberInp, setCustomerNumberInp] = useState(false);
  const [customerGroup, setCustomerGroup] = useState(formData?.assignInfo?.customerGroup);
  const [sequence, setSequence] = useState(0);

  const organizationRef = useRef(null);

  const facilities = facilitiesData?.data?.facilities ?? [];
  const optionData = useFacilityOptions(facilities);

  const defaultFacilities = useCallback(() => {
    const facilities = [];
    formData?.assignInfo?.organization?.soldToNumbers?.forEach((soldTo) => {
      const soldToFacility = facilitiesData?.data?.facilities?.find((f) => f.facilityNumber === soldTo.soldToNumber);
      let soldToFacilityLabel, shipToFacilityLabel;
      if (soldToFacility) {
        soldToFacilityLabel = getFacilityLabelByName(soldToFacility);
      }
      const shipToFacilities = facilitiesData?.data?.facilities?.filter((f) => soldTo.shipToNumbers.includes(f.facilityNumber));
      if (shipToFacilities && shipToFacilities.length > 0) {
        shipToFacilities.forEach((shipToFacility) => {
          shipToFacilityLabel = getFacilityLabelByName(shipToFacility);
          facilities.push({ cat: soldToFacilityLabel, key: shipToFacilityLabel });
        });
      }
    });
    return facilities;
  }, [facilitiesData?.data?.facilities, formData?.assignInfo?.organization?.soldToNumbers]);

  useEffect(() => {
    setOrg(defaultFacilities());
  }, [defaultFacilities]);

  useEffect(() => {
    const assignInfo = formData?.assignInfo;

    if (!assignInfo) {
      dispatch(reSetFacilitiesData({}));
      return;
    }

    const { customerNumber, customerGroup, feature, userEmail } = assignInfo;

    if (customerNumber) {
      dispatch(getFacilitiesAsync(customerNumber));
      setEditable(false);
    } else if (!customerGroup) {
      dispatch(reSetFacilitiesData({}));
    }

    if (feature === "Survey" && Array.isArray(userEmail)) {
      const emailTags = userEmail.map(({ id, email }) => ({
        value: id,
        label: email,
      }));
      setEmailTags(emailTags);
    }
  }, [formData?.assignInfo, dispatch]);

  const handleEdit = useCallback(() => {
    const assignInfo = formData?.assignInfo;
    setErrorMsg("");
    setEditable(true);
    setRemoveDefaultOrg(true);
    setOrg(defaultFacilities);

    const customerNumber = assignInfo?.customerNumber ?? "";
    const customerGroup = assignInfo?.customerGroup;
    const feature = assignInfo?.feature;
    const userEmail = assignInfo?.userEmail;
    const hasCustomerNumber = Boolean(customerNumber);
    setCustomerNumber(customerNumber);

    if (customerGroup != null) {
      setCreateGroup(true);
      setCustomerNumberInp(false);
    } else if (hasCustomerNumber) {
      setCreateGroup(false);
      setCustomerNumberInp(true);
    } else {
      setCreateGroup(false);
      setCustomerNumberInp(false);
    }

    if (feature === "Survey" && Array.isArray(userEmail)) {
      setEmailTags(
        userEmail.map((email) => ({
          value: email,
          label: email,
        }))
      );
    } else {
      setEmailTags([]);
    }
  }, [defaultFacilities, formData?.assignInfo]);

  const handleCancel = useCallback(() => {
    setEditable(false);
    setErrorAssign(false);
  }, []);

  const onSubmit = () => {
    setUpdateStatus({});

    const emailIDs = Array.isArray(emailTags) ? emailTags.map(({ label  }) => label) : [];
    const soldToMap = new Map();

    org.forEach((item) => {
      const soldToNumber = item?.facilityNumber;
      if (!soldToNumber) return;

      if (!soldToMap.has(soldToNumber)) {
        soldToMap.set(soldToNumber, {
          soldToNumber,
          shipToNumbers: [],
        });
      }

      const shipToNumber = item?.facilityNumber;
      if (shipToNumber) {
        soldToMap.get(soldToNumber).shipToNumbers.push(shipToNumber);
      }
    });

    const soldToNumbers = Array.from(soldToMap.values());

    const isCustomerGroup = customerGroup !== null;
    const feature = formData?.assignInfo?.feature;

    const tempUserData = {
      customerGroup,
      customerNumber: isCustomerGroup ? null : customerNumber,
      organization: isCustomerGroup
        ? null
        : {
            primaryGroupNumber: facilitiesData?.data?.facilities?.find((f) => f.accountGroup === "Z002")?.facilityNumber ?? null,
            soldToNumbers,
          },
      feature,
      subFeature: feature === "NE1 WMS" ? formData?.assignInfo?.subFeature : null,
      sequence,
      ...(feature === "Survey" && { userEmail: emailIDs }),
    };

    const result = {
      ...formData,
      assignInfo: {
        ...formData?.assignInfo,
        ...tempUserData,
      },
    };

    const sectionMap = Array.isArray(result.sectionInfo)
      ? result.sectionInfo.reduce((acc, { sectionId, name }) => {
          acc[sectionId] = name;
          return acc;
        }, {})
      : {};

    result.formBuilderInfo = Array.isArray(result.formBuilderInfo)
      ? result.formBuilderInfo.map((item) => ({
          ...item,
          sectionName: sectionMap[item.sectionId] ?? null,
        }))
      : [];

    dispatch(upDateformDetailsAsync(result));
    setEditable(false);
    setEmailTags([]);
  };

  const onChangeOrg = useCallback((selectedList) => {
    setErrorAssign(false);
    setOrg([...selectedList]);
    setRemoveDefaultOrg(false);
  }, []);

  const getOrganization = useCallback(() => {
    setEmailTags([]);
    const customerNum = document.getElementById("customerNumber")?.value;
    if (customerNum) {
      setOrg([]);
      setRemoveDefaultOrg(false);
      dispatch(reSetFacilitiesData({}));
      setCustomerNumber(customerNum);
      dispatch(getFacilitiesAsync(customerNum));
      setCreateGroup(false);
      setCustomerNumberInp(true);
    }
  }, [dispatch]);

  const enterCustomerNumber = useCallback(
    (e) => {
      const value = e.target.value;
      setEmailTags([]);

      if (!value) {
        setOrg([]);
        setRemoveDefaultOrg(false);
        dispatch(reSetFacilitiesData({}));
        setCustomerNumber(null);
        setCreateGroup(false);
        setCustomerNumberInp(false);
        return;
      }

      if (e.key === "Enter") {
        setOrg([]);
        setRemoveDefaultOrg(false);
        dispatch(reSetFacilitiesData({}));
        setCustomerNumber(value);
        dispatch(getFacilitiesAsync(value));
        setCreateGroup(false);
        setCustomerNumberInp(true);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    let fac = "";
    if (facilitiesData?.errorMessage === "Invalid Customer Number.") {
      fac = "Customer number doesn't match our record";
    } else if (facilitiesData?.data !== undefined) {
      if (optionData.length === 0) {
        fac = "Organization mapping is missing for the customer";
      }
    }
    setErrorMsg(fac);
  }, [facilitiesData?.data, facilitiesData?.errorMessage, optionData.length]);

  const getCustomerGroup = useCallback((e) => {
    setEmailTags([]);
    if (e.target.value !== "") {
      setCustomerGroup(e.target.value);
      setCreateGroup(true);
      document.getElementById("customerNumber").value = "";
      setErrorAssign(false);
      setRemoveDefaultOrg(false);
    } else {
      setCustomerGroup(null);
      setCreateGroup(false);
    }
  }, []);

  const handleChangeEmail = useCallback((selectedOptions) => {
    const finalTags = selectedOptions || [];
    setEmailTags(finalTags);
  
    if (finalTags.length === 0) {
      setCreateGroup(false);
      setCustomerNumberInp(false);
    }
  }, []);

  const onLoadEmail = useCallback((query, callback) => {
    if (!query) {
      callback([]);
      return;
    }
  
    setOrg([]);
    setRemoveDefaultOrg(false);
    dispatch(reSetFacilitiesData({}));
    setCustomerNumber("");
    setCustomerNumberInp(true);
    document.getElementById("customergroup").value = "";
    setCustomerGroup(null);
    setCreateGroup(false);
    document.getElementById("customerNumber").value = "";
  
    dispatch(getAssignEmailAsyn({ query, callback }));
  }, [dispatch]);

  if (isFormLoading) {
    return (
      <div className={`${"loader"} col-12`}>
        <img src={Loader} alt="loading" />
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.tab_content_top}`}>
        <h2 className={`${styles.tab_content_hdg}`}>
          {formData.assignInfo && formData.name}
          {!editable && (
            <span className={`float-sm-right ${styles.tab_edit} handleEdit`} onClick={handleEdit}>
              <img src={editIcon} alt="edit" />
              {EDIT}
            </span>
          )}
        </h2>
      </div>
      {editable && errorAssign && (
        <span className={`${styles.error_message} text-center`}>
          {formData?.assignInfo?.feature === "NE1 WMS"
            ? "Please select customer number and organization"
            : formData?.assignInfo?.feature === "Survey"
            ? "Please select customer group selection or customer number and organization or email"
            : "Please select customer group selection or customer number and organization"}
        </span>
      )}
      <div className={`col-md-12 ${styles.single_product_block}`}>
        <div className="row">
          <div className={`p-0 ${styles.patient_content}`}>
            {editable && (
              <Form className={`${styles.patient_content} onsubmit`} onSubmit={handleSubmit(onSubmit)} requiredPermission={PERMISSIONS.ADMIN_FORM_BUILDER}>
                {formData?.assignInfo?.feature !== "NE1 WMS" && (
                  <>
                    <SelectBox
                      id={"customergroup"}
                      name={"customergroup"}
                      label={`Customer Group Selection:`}
                      classes={{
                        column: `col-12 ${styles.col_spec}`,
                        inputClassName: styles.basic_select,
                        labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                        inputColumn: `col-3 p-0 ${styles.select_wrapper}`,
                      }}
                      optionValue={[
                        { key: "all", value: "All Customers" },
                        { key: "postacute", value: "Post-Acute" },
                        { key: "acute", value: "Acute" },
                      ]}
                      defaultValue={formData.assignInfo && formData.assignInfo.customerGroup}
                      errors={errors}
                      placeholder="Select"
                      onChange={(e) => getCustomerGroup(e)}
                      disabled={customerNumberInp ? true : false}
                    />
                    <div className="offset-4 mb-3">
                      <strong>OR</strong>
                    </div>
                  </>
                )}

                {formData?.assignInfo?.feature === "Survey" && (
                  <>
                    <div className={`col-12 ${styles.col_spec}`}>
                      <label className={`col-3 col-form-label ${styles.label_spec}`}>Email: </label>
                      <div className="col-7 p-0">
                        <AsyncTagSelect
                          tags={emailTags}
                          onLoad={onLoadEmail}
                          handleChange={handleChangeEmail}
                          placeholder="Type to search email..."
                          inputId="emailDomain"
                          noOptionMessage="Entered email is not available"
                          classNamePrefix="tag-select"
                         />
                      </div>
                    </div>

                    <div className="offset-4 mb-3">
                      <strong>OR</strong>
                    </div>
                  </>
                )}
                <TextField
                  id={"customerNumber"}
                  name={"customerNumber"}
                  label={"Customer Number:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    labelClass: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 text-nowrap`,
                    inputClassName: `${styles.basic_input} col-12 customerNum`,
                  }}
                  type="text"
                  note={
                    <span
                      className={`col-3 col-form-label ${styles.form_span} getorg ${createGroup ? styles.disabled : ""}`}
                      onClick={() => getOrganization()}
                    >
                      Search <img src={rightArrow} alt="" className={`${styles.img_left}`} />
                    </span>
                  }
                  onKeyUp={(e) => enterCustomerNumber(e)}
                  defaultValue={formData?.assignInfo?.customerNumber}
                  customError={createGroup ? "" : errorMsg}
                  disabled={createGroup ? true : false}
                />

                <MultiSelect
                  id={"organization"}
                  name={"organization"}
                  label={"Organization:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 ${styles.label_spec}`,
                    inputColumn: `col-5 p-0`,
                  }}
                  optionValue={optionData}
                  validator={(e) => {
                    organizationRef.current = e;
                  }}
                  placeholder="Select"
                  showCheckbox={true}
                  displayValue="cat"
                  showSelectAll
                  onSelect={onChangeOrg}
                  onRemove={onChangeOrg}
                  defaultValue={removeDefaultOrg ? defaultFacilities() : Array.isArray(org) ? org : []}
                />

                <SelectBox
                  id={"feature"}
                  name={"feature"}
                  label={`Feature:`}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 ${styles.select_wrapper}`,
                  }}
                  optionValue={[
                    { key: "NE1 WMS", value: "NE1 WMS" },
                    { key: "Product Selector", value: "Product Selector" },
                    { key: "Survey", value: "Survey" },
                  ]}
                  defaultValue={formData?.assignInfo?.feature}
                  disabled={true}
                />
                <SelectBox
                  id={"subFeature"}
                  name={"subFeature"}
                  label={`Sub-Feature:`}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    inputClassName: styles.basic_select,
                    labelClassName: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: `col-5 p-0 ${styles.select_wrapper}`,
                  }}
                  optionValue={[
                    { key: "null", value: "Select" },
                    { key: "Characteristics", value: "Characteristics" },
                    { key: "Treatment", value: "Treatment" },
                  ]}
                  defaultValue={formData.assignInfo && formData.assignInfo.subFeature}
                  disabled={true}
                />
                <TextField
                  id={"sequence"}
                  name={"sequence"}
                  label={"Sequence:"}
                  classes={{
                    column: `col-12 ${styles.col_spec}`,
                    labelClass: `col-3 col-form-label ${styles.label_spec}`,
                    inputColumn: "col-5 p-0",
                    inputClassName: `${styles.basic_input} `,
                  }}
                  type="number"
                  defaultValue={formData.assignInfo && formData.assignInfo.sequence}
                  disabled={formData?.assignInfo?.feature !== "NE1 WMS" ? true : false}
                  onKeyUp={(e) => setSequence(e.target.value)}
                />
                <div className="col-sm-12">
                  <div className="text-center pb-4">
                    <br />
                    {/* Cancel button  */}
                    <Button classes={{ className: `btn mr-3 ${styles.btn_custom} ${styles.btn_small}` }} type="submit" id={SUBMIT_ID}>
                      {SAVE_BTN}
                    </Button>
                    <Button
                      classes={{ className: `btn ${styles.btn_custom} ${styles.btn_small_cancel} handleCancel` }}
                      id={CANCEL_ID}
                      onClick={handleCancel}
                    >
                      {CANCEL_BTN}
                    </Button>

                    {/* Save Button */}
                  </div>
                </div>
              </Form>
            )}
            {!editable && <ViewAssign />}
          </div>
        </div>
      </div>
    </>
  );
};
